import { useTranslation } from "react-i18next";
import React, {useEffect, useMemo, useState, useTransition} from "react";
import OverviewChartTab from "../chart/overview-tab/OverviewChartTab";
import OverviewTable from "../table/overview-table";
import dayjs from "dayjs";
import {staticReportStore} from "../../../../../stores/stores";
import {
  getAdminVisitors,
  getAdminVisits,
  getCourseByUniversitySponsor,
  getStudentByUniversity,
  getSynthesizeDataUniversity,
  getTeacherByUniversity,
  getUnitResources,
  getUnitResourcesTable,
  getUniversityRealtion,
  getVisitsAndVisitors,
  getVisitsAndVisitorsByRegister
} from "../../../../../service/statistical-report";
import {debounce} from "lodash";
import {BarStackedChartProps, DualChart, PieChart} from "../../../../../types/static-report";
import {renderColDualChart} from "../../../../../utils/staticReport";

type Props = {
  dataType: string;
};

const OverviewTab = ({dataType}: Props) => {
  const { t } = useTranslation();
  const [isPending, startTransition] = useTransition();
  const {staticReportType, filterValues, tab, isSelectedUniversity} = staticReportStore();

  const [chartData, setChartData] = useState({
    visitsAndVisitors: [] as DualChart[],
    visitsAndVisitorsByRegister: [] as PieChart[],
    adminVisits: [] as Array<any>,
    adminVisitors: [] as Array<any>,
    universityRelation: [] as Array<any>,
    teacherByUniversity: [] as BarStackedChartProps[],
    studentByUniversity: [] as BarStackedChartProps[],
    courseByUniversitySponsor: [] as BarStackedChartProps[],
    courseByUniversitySponsorTotal: 0,
    unitResources: [] as Array<any>,
    unitResourceTable: [] as Array<any>,
    synthesizeDataUniversity: [] as Array<any>,
  });

  const [loading, setLoading] = useState({
    visitsAndVisitors: false,
    visitsAndVisitorsByRegister: false,
    universityRelationList: false,
    teacherByUniversity: false,
    studentByUniversity: false,
    courseByUniversitySponsor: false,
    unitResources: false,
  });
  const fetchData = async () => {
    const payload = {

      from: dayjs(filterValues.dateFrom).toISOString(),
      to: dayjs(filterValues.dateTo).toISOString(),
      courseLevelIds: filterValues?.courseLevelIds,
      industryGroupIds: filterValues?.industryGroupIds,
      courseIds: filterValues?.courseIds,
      universityIds: isSelectedUniversity ? filterValues.universityIds : [],
      timeUnit: filterValues.timeUnit
    };
    try {
      const apiCall = [
        {
          key: "visitsAndVisitors",
          total: "visitsAndVisitorsTotal",
          api: getVisitsAndVisitors,
          params: payload,
        },
        {
          key: "visitsAndVisitorsByRegister",
          total: "visitsAndVisitorsByRegisterTotal",
          api: getVisitsAndVisitorsByRegister,
          params: payload,
        },
        {
          key: "universityRelation",
          total: "universityRelationTotal",
          api: getUniversityRealtion,
          params: payload,
        },
        {
          key: "teacherByUniversity",
          total: "teacherByUniversityTotal",
          api: getTeacherByUniversity,
          params: payload,
        },
        {
          key: "studentByUniversity",
          total: "studentByUniversityTotal",
          api: getStudentByUniversity,
          params: payload,
        },
        {
          key: "adminVisits",
          total: "adminVisitsTotal",
          api: getAdminVisits,
          params: payload,
        },
        {
          key: "adminVisitors",
          total: "adminVisitorsTotal",
          api: getAdminVisitors,
          params: payload,
        },
        {
          key: "courseByUniversitySponsor",
          total: "courseByUniversitySponsorTotal",
          api: getCourseByUniversitySponsor,
          params: payload,
        },
        {
          key: "unitResources",
          total: "unitResourcesTotal",
          api: getUnitResources,
          params: payload,
        },
        {
          key: 'unitResourceTable',
          total: "unitResourceTableTotal",
          api: getUnitResourcesTable,
          params: payload,
        },
        {
          key: "synthesizeDataUniversity",
          total: "synthesizeDataUniversityTotal",
          api: getSynthesizeDataUniversity,
          params: payload,
        },
      ]
      await Promise.allSettled(
        apiCall.map(async ({key, api, total, params}) => {
          setLoading((prev) => ({...prev, [key]: true, [total]: true}));
          try {
            const response = await api(params);
            startTransition(() => {
              setChartData((prevState) => ({
                ...prevState,
                [key]: response?.data?.data?.statistic || response?.data?.data,
                [total]: response?.data?.data?.total ? response?.data?.data?.total : null,
              }));
            })
          } catch (error) {
            console.error(`Error fetching data for ${key}:`, error);
            startTransition(() => {
              setChartData(prevState => ({
                ...prevState,
                [key]: [], // Provide fallback empty data
              }));
            });
          } finally {
            setLoading((prev) => ({...prev, [key]: false}));
          }
        })
      );
    } catch (err) {
      console.error(err)
    }
  }

  const debouncedFetchData = useMemo(
    () => debounce(fetchData, 500),
    [filterValues]
  );

  useEffect(() => {
    if (tab === "1") {
      debouncedFetchData();
    }
    return () => {
      debouncedFetchData.cancel();
    };
  }, [filterValues, tab]);

  const memoizedChartData = useMemo(() => {
    const series = [t('Vãng lai', 'Vãng lai'), t('Người dùng tự do', 'Người dùng tự do'), t('Trường đăng ký', 'Trường đăng ký')];
    const series2 = [t('Cử nhân', 'Cử nhân'), t('Thạc sĩ', 'Thạc sĩ'), t('Tiến sĩ', 'Tiến sĩ'), t('Phó giáo sư', 'Phó giáo sư'), t('Giáo sư', 'Giáo sư'), t('Khác', 'Khác')];
    const series3 = [t('Học viên đăng ký mới', 'Học viên đăng ký mới'), t('Học viên cũ', 'Học viên cũ')];
    const series4 = [t('Phối hợp', 'Phối hợp'), t('Chủ trì', 'Chủ trì')];
    const series5 = [t('Bài kiểm tra', 'Bài kiểm tra'), t('Đa phương tiện', 'Đa phương tiện'), t('Tài liệu tham khảo', 'Tài liệu tham khảo'), t('Scorm & xAPI', 'Scorm & xAPI')];
    renderColDualChart(series, chartData.adminVisitors)
    renderColDualChart(series, chartData.adminVisits)
    renderColDualChart(series2, chartData.teacherByUniversity)
    renderColDualChart(series3, chartData.studentByUniversity)
    renderColDualChart(series4, chartData.courseByUniversitySponsor)
    renderColDualChart(series5, chartData.unitResourceTable)
    return {
      visitsAndVisitors: chartData.visitsAndVisitors,
      visitsAndVisitorsByRegister: chartData.visitsAndVisitorsByRegister,
      adminVisits: chartData.adminVisits,
      adminVisitors: chartData.adminVisitors,
      universityRelationList: chartData.universityRelation,
      teacherByUniversity: chartData.teacherByUniversity,
      studentByUniversity: chartData.studentByUniversity,
      courseByUniversitySponsor: chartData.courseByUniversitySponsor,
      courseByUniversitySponsorTotal: chartData.courseByUniversitySponsorTotal,
      unitResources: chartData.unitResources,
      unitResourceTable: chartData.unitResourceTable,
      synthesizeDataUniversity: chartData.synthesizeDataUniversity,
    };
  }, [chartData]);

  return (
    dataType === "1" ?
      <OverviewChartTab {...memoizedChartData} loading={loading}/> :
      <OverviewTable {...memoizedChartData} loading={isPending}/>
  );
};

export default OverviewTab;
